import { SessionEstimate } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { Box, Card } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { SessionEstimateDataRow } from './SessionEstimateDataRow';
import { SessionEstimateFooter } from './SessionEstimateFooter';
import { SessionEstimateHeader } from './SessionEstimateHeader';
import { TitleRow } from './TitleRow';
import { byPosition } from '@talon/web/src/utils/stableOrder';

export interface ISessionEstimatePanelProps {
  sessionEstimate: SessionEstimate;
}

export const SessionEstimatePanel = observer(({ sessionEstimate }: ISessionEstimatePanelProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();

  /** State **/
  const [isUpdatingSession, setIsUpdatingSession] = useState(false);

  /** Computed **/
  const estimateItems = useMemo(() => {
    const items = sessionEstimate.items;
    return items && [...items].sort(byPosition);
  }, [sessionEstimate.items]);

  /** Methods **/

  /**
   * Update this panel's SessionEstimate
   * @param sessionEstimate
   * @param sessionEstimateIndex
   * @returns
   */
  const updateSessionEstimate = async (sessionEstimate: SessionEstimate) => {
    if (isUpdatingSession) return;
    else if (!estimateStore.currentEstimate) {
      console.warn('No estimate loaded!');
      return;
    }

    setIsUpdatingSession(true);
    // The header puts an edited date on the SessionEstimate itself; sending session.date (as this
    // did) overwrote the edit with the date it already had
    await estimateStore.updateSessionEstimate(
      sessionEstimate.id,
      sessionEstimate.title,
      sessionEstimate.date ?? sessionEstimate.session?.date
    );
    setIsUpdatingSession(false);
  };

  const addEstimateItem = async () => {
    await estimateStore.createSessionEstimateItem(sessionEstimate.id);
  };

  /** Render **/
  return (
    <Card margin="small" key={sessionEstimate.id} fill="horizontal" background="white">
      {/* Header */}
      <SessionEstimateHeader
        sessionEstimate={sessionEstimate}
        setSessionEstimate={(sessionEstimate) => updateSessionEstimate(sessionEstimate)}
      />

      {/* Data Table */}
      <Box border={{ side: 'bottom', size: 'xsmall' }}>
        <TitleRow isUnion={sessionEstimate.session?.is_union} />
        {estimateItems.map((item, i) => (
          <SessionEstimateDataRow
            key={item.id}
            sessionEstimateItem={item}
            index={i}
            useColors
            isUnion={sessionEstimate.session?.is_union}
          />
        ))}
      </Box>

      {/* Footer */}
      <SessionEstimateFooter addEstimateItem={addEstimateItem} />
    </Card>
  );
});
